import React, { useLayoutEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Icon } from '../../icons';
import { LinkButton, ButtonLink } from '../../atoms';
import { AddPupButton } from '../AddPupButton';
import {
  SlidingContainer,
  CtaButton,
  SlideoutMenuLink,
  ResponsiveImage,
} from '../../components';
import tagIcon from '../../images/tag.svg';
import { useSessionCustomer } from '../../customer';
import { useSession } from '../../api';
import { useToast, TOAST_CLIENT_ERROR_MESSAGE } from '../../Toast';
import { ReferAFriendLink, HeaderLink } from './HeaderLink';
import {
  DesktopNavigationMenu,
  MobileNavigationMenu,
  BurgerMenu,
  SlideOutMenu,
  GetStartedButton,
  MobileCtaButtonContainer,
  MobileButtonWrapper,
  NavigationMenuLink,
  SlideOutMenuTopSection,
  SlideOutMenuCloseButton,
  LogoImgContainer,
} from './NavigationBar/styles.js';
import { SmallNavLink } from './styles';
import OllieLogo from './NavigationBar/ollie-logo.svg';

export const LoginMenuLinks = () => (
  <>
    <GetStartedButton>
      <LinkButton variant="egg" verticalPadding={3} href="/onboarding/">
        Get started
      </LinkButton>
    </GetStartedButton>
    <Link href="/auth/login/" passHref>
      <SmallNavLink>Login</SmallNavLink>
    </Link>
  </>
);

export const LoggedInMenuLink = () => {
  const { customer } = useSessionCustomer();

  return (
    <>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src={tagIcon.src} alt="dog tag" />
      <Link href="/account/" passHref>
        <SmallNavLink>Hi, {customer?.first_name ?? 'friend'}</SmallNavLink>
      </Link>
    </>
  );
};

export const UserMenuLinks = () => {
  const { isActive } = useSessionCustomer();
  return isActive ? <LoggedInMenuLink /> : <LoginMenuLinks />;
};

export const LeftMenuLinks = ({ links }) => {
  const [showSideMenu, setShowSideMenu] = useState(false);
  const [slidingContainerOffset, setSlidngContainerOffset] = useState(0);
  const { isActive } = useSessionCustomer();

  const toggleSideMenu = () => {
    setShowSideMenu(!showSideMenu);
  };

  useLayoutEffect(() => {
    const nav = document.querySelectorAll('[role="navigation"]')[0];
    const { top, height } = nav.getBoundingClientRect();
    setSlidngContainerOffset(top + height);
  }, []);

  const { logout } = useSession();
  const router = useRouter();
  const { showToast } = useToast();

  const onLogoutClick = async () => {
    const { Gladly } = window;
    if (Gladly) Gladly.clearUser();
    try {
      await logout();
      if (router.pathname.includes('/account')) {
        router.push('/auth/login/');
      }
    } catch {
      showToast({
        status: 'error',
        headline: 'Error',
        message: TOAST_CLIENT_ERROR_MESSAGE,
      });
    }
  };

  return (
    <>
      <MobileNavigationMenu>
        <BurgerMenu onClick={toggleSideMenu}>
          {showSideMenu ? (
            <Icon iconName="close" alt="burger menu" size={1} id="close_menu" />
          ) : (
            <Icon iconName="menu" alt="burger menu" size={1} id="show_menu" />
          )}
        </BurgerMenu>
        <SlidingContainer
          active={showSideMenu}
          onExit={toggleSideMenu}
          bgColor="background.base"
          alignment="left"
          offsetTop={slidingContainerOffset}
        >
          <>
            <SlideOutMenu>
              <SlideOutMenuTopSection>
                <SlideOutMenuCloseButton onClick={toggleSideMenu}>
                  <Icon
                    iconName="close"
                    alt="burger menu"
                    size={1}
                    id="close_menu_slide"
                  />
                </SlideOutMenuCloseButton>

                <LogoImgContainer>
                  <ResponsiveImage
                    src={OllieLogo.src}
                    alt="Ollie logo"
                    width={OllieLogo.width}
                    height={OllieLogo.height}
                  />
                </LogoImgContainer>

                {!isActive && (
                  <div>
                    <Link href="/auth/login/" passHref>
                      <SmallNavLink>Login</SmallNavLink>
                    </Link>
                  </div>
                )}
              </SlideOutMenuTopSection>
              {links.map((link, index) => {
                const { component } = link;
                if (component === 'slideout_menu_link')
                  return (
                    <NavigationMenuLink>
                      <SlideoutMenuLink
                        {...link}
                        key={index}
                        onCloseMenu={toggleSideMenu}
                      />
                    </NavigationMenuLink>
                  );
                if (component === 'refer_a_friend_button')
                  return (
                    <NavigationMenuLink>
                      <ReferAFriendLink
                        key={index}
                        position={index}
                        {...link}
                        type={'mobile'}
                      />
                    </NavigationMenuLink>
                  );
                return (
                  <NavigationMenuLink>
                    <HeaderLink
                      target={link.target}
                      position={index}
                      text={link.text}
                      type="desktop"
                      key={index}
                      onClick={toggleSideMenu}
                    />
                  </NavigationMenuLink>
                );
              })}
              {isActive && (
                <>
                  <MobileButtonWrapper verticalMargin={7}>
                    <AddPupButton
                      href="/account/add-pup/"
                      fullWidth={true}
                      variant="blueberry"
                      verticalPadding={3}
                      horizontalPadding={3}
                      text="Add Pup"
                    />
                  </MobileButtonWrapper>
                  <MobileButtonWrapper verticalMargin={2}>
                    <ButtonLink
                      onClick={onLogoutClick}
                      variant="hero.cranberry"
                    >
                      Logout
                    </ButtonLink>
                  </MobileButtonWrapper>
                </>
              )}
            </SlideOutMenu>
            {!isActive && (
              <MobileCtaButtonContainer>
                <CtaButton href="/onboarding/" variant="egg" fullWidth={true}>
                  Get started
                </CtaButton>
              </MobileCtaButtonContainer>
            )}
          </>
        </SlidingContainer>
      </MobileNavigationMenu>
    </>
  );
};

LeftMenuLinks.displayName = 'MarketingMenuLinks';

LeftMenuLinks.propTypes = {
  links: PropTypes.arrayOf(
    PropTypes.shape({
      target: PropTypes.string,
      text: PropTypes.string,
      component: PropTypes.string,
    }),
  ),
};

export const CenterMenuLinks = ({ links }) => {
  return (
    <>
      <DesktopNavigationMenu>
        {links.map((link, index) => {
          const { component } = link;
          if (component === 'slideout_menu_link')
            return (
              <NavigationMenuLink key={index}>
                <SlideoutMenuLink {...link} />
              </NavigationMenuLink>
            );
          if (component === 'refer_a_friend_button')
            return (
              <NavigationMenuLink key={index}>
                <ReferAFriendLink position={index} {...link} type={'desktop'} />
              </NavigationMenuLink>
            );
          return (
            <NavigationMenuLink key={index}>
              <HeaderLink {...link} position={index} type="desktop" />
            </NavigationMenuLink>
          );
        })}
      </DesktopNavigationMenu>
    </>
  );
};

CenterMenuLinks.displayName = 'CenterMenuLinks';

CenterMenuLinks.propTypes = {
  links: PropTypes.arrayOf(
    PropTypes.shape({
      target: PropTypes.string,
      text: PropTypes.string,
      component: PropTypes.string,
    }),
  ),
};
